<script setup lang="ts">
  import { ref, watch, nextTick, useTemplateRef, type Component } from 'vue'
  import type { ResolvedField } from '../../model/types'
  import { useDraggable } from 'vue-draggable-plus'
  import {
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection
  } from '@gat-ship/vue-components'
  import {
    IconGripVertical,
    IconArrowUp,
    IconArrowDown,
    IconTextCursor,
    IconAlignLeft,
    IconHash,
    IconCalendarDays,
    IconSquareCheck,
    IconChevronDown,
    IconPanelTop,
    IconMinus
  } from '@gat-ship/icons'

  const props = defineProps<{
    fields: ResolvedField[]
  }>()

  const emit = defineEmits<{
    save: [orderedIds: string[]]
  }>()

  const open = defineModel<boolean>('open', { default: false })

  const localFields = ref<ResolvedField[]>([])
  const listRef = useTemplateRef<HTMLElement>('listRef')

  const { start } = useDraggable(listRef, localFields, {
    handle: '.drag-handle',
    animation: 150,
    immediate: false
  })

  watch(open, async (val) => {
    if (val) {
      localFields.value = [...props.fields]
      await nextTick()
      start()
    }
  })

  const fieldTypeIcons: Record<string, Component> = {
    text: IconTextCursor,
    multiline: IconAlignLeft,
    number: IconHash,
    datetime: IconCalendarDays,
    checkbox: IconSquareCheck,
    dropdown: IconChevronDown,
    section: IconPanelTop
  }

  function getFieldTypeIcon(field: ResolvedField): Component {
    const type = field.fieldType ?? field.inputType
    if (!type) return IconMinus
    return fieldTypeIcons[type] ?? IconMinus
  }

  function moveUp(index: number) {
    if (index <= 0) return
    const items = [...localFields.value]
    const temp = items[index]!
    items[index] = items[index - 1]!
    items[index - 1] = temp
    localFields.value = items
  }

  function moveDown(index: number) {
    if (index >= localFields.value.length - 1) return
    const items = [...localFields.value]
    const temp = items[index]!
    items[index] = items[index + 1]!
    items[index + 1] = temp
    localFields.value = items
  }

  function save() {
    const orderedIds = localFields.value
      .map((f) => f.customFieldId)
      .filter(Boolean) as string[]
    emit('save', orderedIds)
    open.value = false
  }
</script>

<template>
  <modal v-model:open="open" type="dialog">
    <ModalHeader>
      <ModalHeaderTitle>Reorder Custom Fields</ModalHeaderTitle>
      <ModalHeaderDescription>
        Drag fields or use the arrow buttons to change the display order.
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalSection>
      <ModalContent>
        <div ref="listRef" class="flex flex-col">
          <div
            v-for="(field, index) in localFields"
            :key="field.fieldName"
            class="border-border flex items-center gap-3 border-b p-3 last:border-b-0"
          >
            <IconGripVertical
              class="drag-handle text-muted-foreground h-4 w-4 shrink-0 cursor-grab active:cursor-grabbing"
            />
            <component
              :is="getFieldTypeIcon(field)"
              class="text-muted-foreground h-4 w-4 shrink-0"
            />
            <span class="flex-1 truncate text-sm font-medium">
              {{ field.label }}
            </span>
            <Button
              size="icon"
              variant="ghost"
              :disabled="index === 0"
              @click="moveUp(index)"
            >
              <IconArrowUp class="size-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              :disabled="index === localFields.length - 1"
              @click="moveDown(index)"
            >
              <IconArrowDown class="size-4" />
            </Button>
          </div>
        </div>
      </ModalContent>
    </ModalSection>
    <ModalFooter>
      <Button @click="save">Save Order</Button>
      <Button variant="ghost" @click="open = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
